import { createApiSlice } from '@/shared/features/query/config/query.build';
import type { ReduxQueryClient } from '@/shared/features/query/domain/interfaces/QueryClient';
import type { AppDispatch } from '@/app/store/store';
import { executeQuery } from '@/shared/features/query/utils/queryUtils';
import { getApiBaseUrl } from '@/core/utils/apiUtils';
import type { PdpStoreRepository } from '../../domain/interfaces/PdpStoreRepository';
import type { PdpNearbyStore } from '../../domain/models/PdpNearbyStore';
import type { PdpStoreForCustomization } from '../../domain/models/PdpStoreForCustomization';
import type {
  PdpNearbyStoresResponseDto,
  PdpStoreForCustomizationDto,
} from '../dto/pdpStore.dto';
import {
  nearbyStoresDtoToDomain,
  storeForCustomizationDtoToDomain,
} from '../mappers/pdpStore.mapper';

export type GetStoreForCustomizationArgs = {
  pincode: string;
  customizationId: number;
};

export type GetNearbyStoresArgs = {
  pincode: string;
};

const pdpStoreApiSlice = createApiSlice({
  clientId: 'pdpStoreApi',
  tagTypes: ['PDPStore'],
  endpoints: {
    getStoreForCustomization: {
      url: (args: unknown) => {
        const { pincode, customizationId } = (args || {}) as GetStoreForCustomizationArgs;
        return `${getApiBaseUrl()}/physical-store/store-for-customization?pincode=${encodeURIComponent(pincode)}&customizationId=${encodeURIComponent(String(customizationId))}`;
      },
      method: 'GET',
      transformResponse: (response: unknown): PdpStoreForCustomization | null => {
        const dto = (response ?? {}) as PdpStoreForCustomizationDto;
        return storeForCustomizationDtoToDomain(dto);
      },
      keepUnusedDataFor: 0,
    },
    getNearbyStores: {
      url: (args: unknown) => {
        const { pincode } = (args || {}) as GetNearbyStoresArgs;
        return `${getApiBaseUrl()}/physical-store/store-details-for-product-page?pincode=${encodeURIComponent(pincode)}`;
      },
      method: 'GET',
      transformResponse: (response: unknown): Record<string, PdpNearbyStore> => {
        const dto = (response ?? {}) as PdpNearbyStoresResponseDto;
        return nearbyStoresDtoToDomain(dto);
      },
      keepUnusedDataFor: 0,
    },
  },
});

export const pdpStoreApi = pdpStoreApiSlice.queryClient;
export const pdpStoreApiRedux = pdpStoreApiSlice.queryClient as ReduxQueryClient;

export class PdpStoreAdapter implements PdpStoreRepository {
  constructor(private readonly dispatch: AppDispatch) {}

  async fetchStoreForCustomization(
    pincode: string,
    customizationId: number
  ): Promise<PdpStoreForCustomization | null> {
    const initiateResult = pdpStoreApi.endpoints.getStoreForCustomization.initiate({
      pincode,
      customizationId,
    });
    const plain = await executeQuery<PdpStoreForCustomization | null>(initiateResult, this.dispatch);
    return plain ?? null;
  }

  async fetchNearbyStores(pincode: string): Promise<Record<string, PdpNearbyStore>> {
    const initiateResult = pdpStoreApi.endpoints.getNearbyStores.initiate({ pincode });
    const plain = await executeQuery<Record<string, PdpNearbyStore>>(initiateResult, this.dispatch);
    return plain ?? {};
  }
}
